import { HeadingPriority } from "atoms/constants/heading-priority";
import Heading from "atoms/typography/heading";
import { ClassNameConstants } from "constants/classname-constants";
import PublicationTypes from "constants/publication-types";
import { UnicodeCharacterConstants } from "constants/unicode-character-constants";
import AnnexRecord from "models/view-models/annex-record";
import ArticleRecord from "models/view-models/article-record";
import ChapterRecord from "models/view-models/chapter-record";
import PublicationRecord from "models/view-models/publication-record";
import { VirtualListRowProps } from "organisms/virtual-list/virtual-list";
import React, { forwardRef, ReactNode, Ref } from "react";
import CaapsRecordUtils from "utilities/caaps-record-utils";
import PublicationComponentType from "utilities/enumerations/publication-component-type";
import CultureResources from "utilities/interfaces/culture-resources";

interface CaapsItemTitleAreaWithinPanelProps extends VirtualListRowProps {
    titleClassName: string;
    record: ChapterRecord | ArticleRecord | AnnexRecord;
    publication: PublicationRecord;
    publicationComponentType: PublicationComponentType;
    externalId: string;
    children?: ReactNode;
}

const CSS_BASE_CLASS = "c-publication-page-layout__content";

const CaapsItemTitleAreaWithinPanel = forwardRef<
    HTMLElement,
    CaapsItemTitleAreaWithinPanelProps
>((props: CaapsItemTitleAreaWithinPanelProps, ref: Ref<HTMLElement>) => {
    const { record, publication, publicationComponentType, children } = props;

    // -------------------------------------------------------------------------------------------------
    // #region State
    // -------------------------------------------------------------------------------------------------

    const caapsTypeAsString: keyof CultureResources =
        CaapsRecordUtils().getCaapsTypeString(publicationComponentType);

    // #endregion State

    // -------------------------------------------------------------------------------------------------
    // #region Text Elements
    // -------------------------------------------------------------------------------------------------

    // Determine type-specific title structure for regular bookview title and TIA panel title
    let titleText: string | ReactNode;

    switch (publicationComponentType) {
        // Annex A - Title Here
        case PublicationComponentType.Annex:
            titleText = `${record.getDisplayLabel()} ${
                UnicodeCharacterConstants.EmDash
            } ${record.getDisplayTitle()}`;
            break;
        // Article 200 Title Here
        case PublicationComponentType.Article:
            titleText = `${record.getDisplayLabel()} ${record.getTitle()}`;
            break;
        // Just a Title Here (used in chapters)
        default:
            titleText = record.getTitle();
    }

    const renderTitle = (title: string | ReactNode) => (
        <section
            className={`${CSS_BASE_CLASS}__header -${caapsTypeAsString} -title`}
            ref={ref}
            tabIndex={-1}>
            <Heading
                cssClassName={`${CSS_BASE_CLASS}__title`}
                priority={HeadingPriority.Two}>
                {title}
            </Heading>
        </section>
    );

    // #endregion Text Elements

    // -------------------------------------------------------------------------------------------------
    // #region Style
    // -------------------------------------------------------------------------------------------------

    const canHover = publication.type !== PublicationTypes.NEC;
    const hoverClassName = canHover ? ClassNameConstants.Hover : "";
    // #endregion Hover and CSS Modifiers

    return (
        <section
            className={`${CSS_BASE_CLASS}__header -${caapsTypeAsString}`}
            tabIndex={-1}
            ref={ref}>
            <div
                className={`${CSS_BASE_CLASS}__header__main ${hoverClassName}`}>
                <div className={`${CSS_BASE_CLASS}__header__main__title`}>
                    {renderTitle(titleText)}
                </div>
                {children}
            </div>
        </section>
    );
});

export default CaapsItemTitleAreaWithinPanel;
